import PropTypes from 'prop-types';
import React, { useContext, useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import Navigation from './Navigation';
import useWindowSize from '../../hooks/useWindowSize';
import useOutsideClick from '../../hooks/useOutsideClick';
import { ConfigContext } from '../../context/ConfigContext';
import * as actionType from '../../store/actions';

const SIDEBAR_W      = 220;   // px — collapsed icon-only width
const SIDEBAR_W_ICON = 60;    // px — collapsed icon-only width

// Shell stylesheet. Colors come from CSS variables (src/styles/theme.css) so the
// whole Datta/Bootstrap surface follows the light/dark toggle. The dark palette
// equals the values that used to be hardcoded here, so dark mode is unchanged.
const NOVAA_SHELL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');

  /* ── RESET ─────────────────────────────────────────────────── */
  *, *::before, *::after { box-sizing: border-box; }
  html, body { margin: 0 !important; padding: 0 !important; }
  body { background: var(--nd-bg) !important; font-family: 'DM Sans', sans-serif !important; color: var(--nd-text) !important; }
  #root { margin: 0 !important; padding: 0 !important; background: var(--nd-bg) !important; min-height: 100vh !important; }
  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: var(--nd-w10); border-radius: 4px; }

  /* ── KILL OLD TOP BAR ───────────────────────────────────────── */
  .pcoded-header, .navbar.pcoded-header, .pcoded-header.header-blue,
  .pcoded-header.headerpos-fixed {
    display:    none !important;
    height:     0    !important;
    min-height: 0    !important;
    max-height: 0    !important;
    overflow:   hidden !important;
    padding:    0    !important;
    margin:     0    !important;
  }

  /* ── MAIN CONTAINER ─────────────────────────────────────────── */
  .pcoded-main-container {
    margin:     0 0 0 ${SIDEBAR_W_ICON}px !important;
    padding:    0 !important;
    top:        0 !important;
    background: var(--nd-bg) !important;
    width:      calc(100% - ${SIDEBAR_W_ICON}px) !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    height:     100vh !important;
  }

  .pcoded-wrapper, .pcoded-content, .pcoded-inner-content {
    background: transparent !important;
    margin:  0 !important;
    padding: 0 !important;
    width:   100% !important;
    height:  auto !important;
    overflow: visible !important;
  }
  .content-card-wrapper {
    background: transparent !important;
    margin:  0 !important;
    padding: 0 !important;
    border:  none !important;
    box-shadow: none !important;
    width:   100% !important;
    height:  auto !important;
    overflow: visible !important;
  }
  .main-content-card {
    background: transparent !important;
    margin:  0 !important;
    padding: 0 !important;
    border:  none !important;
    box-shadow: none !important;
    width:   100% !important;
    min-height: 0 !important;
  }

  /* ── SIDEBAR ────────────────────────────────────────────────── */
  .pcoded-navbar {
    background:   var(--nd-surface) !important;
    border-right: 1px solid var(--nd-w06) !important;
    box-shadow:   4px 0 24px rgba(0,0,0,0.3) !important;
    width:        ${SIDEBAR_W_ICON}px !important;
    top:          0 !important;
    height:       100vh !important;
    position:     fixed !important;
    overflow-y:   auto !important;
    overflow-x:   hidden !important;
    transition:   width 0.25s cubic-bezier(0.4,0,0.2,1) !important;
  }
  .pcoded-navbar:hover {
    width: ${SIDEBAR_W}px !important;
  }
  .pcoded-navbar .pcoded-menu-caption,
  .pcoded-navbar .nav-item .nav-caption {
    color: var(--nd-tx22) !important;
    font-size: 9px !important; font-weight: 700 !important;
    letter-spacing: 0.18em !important;
    font-family: 'DM Sans', sans-serif !important;
    padding: 18px 16px 5px !important;
  }
  .pcoded-navbar .pcoded-inner-navbar li > a,
  .pcoded-navbar .nav-item > a,
  .pcoded-navbar .pcoded-hasmenu > a {
    color: var(--nd-tx45) !important;
    font-family: 'DM Sans', sans-serif !important;
    font-size: 13px !important; font-weight: 500 !important;
    border-radius: 10px !important;
    margin: 2px 8px !important; padding: 9px 12px !important;
    transition: all 0.18s ease !important;
    background: transparent !important;
  }
  .pcoded-navbar .pcoded-inner-navbar li > a .pcoded-micon,
  .pcoded-navbar .nav-item > a i,
  .pcoded-navbar .nav-item > a svg {
    color: var(--nd-tx30) !important;
    font-size: 16px !important;
    transition: color 0.18s !important;
  }
  .pcoded-navbar .pcoded-inner-navbar li > a:hover,
  .pcoded-navbar .nav-item > a:hover {
    background: var(--nd-w06) !important;
    color: var(--nd-tx85) !important;
  }
  .pcoded-navbar .pcoded-inner-navbar li > a:hover .pcoded-micon,
  .pcoded-navbar .nav-item > a:hover i,
  .pcoded-navbar .nav-item > a:hover svg { color: var(--nd-tx70) !important; }
  .pcoded-navbar .pcoded-inner-navbar li.active > a,
  .pcoded-navbar .nav-item.active > a,
  .pcoded-navbar .pcoded-inner-navbar li > a.active {
    background: linear-gradient(135deg,rgba(59,130,246,0.18),rgba(34,211,165,0.12)) !important;
    color: var(--nd-text) !important;
    border: 1px solid rgba(59,130,246,0.25) !important;
    font-weight: 600 !important;
  }
  .pcoded-navbar .pcoded-inner-navbar li.active > a .pcoded-micon,
  .pcoded-navbar .nav-item.active > a i,
  .pcoded-navbar .nav-item.active > a svg { color: #60A5FA !important; }
  .pcoded-navbar .pcoded-inner-navbar li.active > a:before,
  .pcoded-navbar .nav-item.active > a:before {
    background: linear-gradient(180deg,#3B82F6,#22D3A5) !important;
    width: 3px !important; border-radius: 0 3px 3px 0 !important;
  }
  .pcoded-navbar .pcoded-submenu {
    background: var(--nd-submenu-bg) !important;
    border-radius: 10px !important; margin: 2px 8px !important;
  }
  .pcoded-navbar .pcoded-submenu > li > a {
    font-size: 12px !important; padding: 7px 12px 7px 34px !important;
    color: var(--nd-tx35) !important;
    margin: 1px 0 !important; border-radius: 8px !important;
  }
  .pcoded-navbar .pcoded-submenu > li.active > a,
  .pcoded-navbar .pcoded-submenu > li > a:hover {
    color: #60A5FA !important; background: rgba(96,165,250,0.08) !important;
  }


  /* ── GLOBAL UI ───────────────────────────────────────────────── */
  .card { background: var(--nd-surface) !important; border: 1px solid var(--nd-w06) !important; border-radius: 14px !important; color: var(--nd-text) !important; box-shadow: var(--nd-shadow) !important; }
  .card-header { background: var(--nd-w03) !important; border-bottom: 1px solid var(--nd-w06) !important; color: var(--nd-text) !important; }
  .card-body { color: var(--nd-text) !important; }
  .table { color: var(--nd-text) !important; border-color: var(--nd-w06) !important; }
  .table thead th { background: var(--nd-w04) !important; color: var(--nd-tx40) !important; border-color: var(--nd-w06) !important; font-size: 10px !important; letter-spacing: 0.12em !important; font-weight: 700 !important; }
  .table td { border-color: var(--nd-w05) !important; color: var(--nd-text) !important; }
  .table tbody tr:hover td { background: var(--nd-w03) !important; }
  .form-control, .form-select, input[type="text"], input[type="email"], input[type="password"], input[type="date"], input[type="number"], select, textarea { background: var(--nd-w05) !important; border: 1px solid var(--nd-w10) !important; border-radius: 10px !important; color: var(--nd-text) !important; font-family: 'DM Sans', sans-serif !important; }
  .form-control:focus, .form-select:focus, input:focus, select:focus, textarea:focus { background: var(--nd-w08) !important; border-color: rgba(96,165,250,0.5) !important; box-shadow: 0 0 0 3px rgba(96,165,250,0.1) !important; color: var(--nd-text) !important; }
  .form-control::placeholder { color: var(--nd-tx25) !important; }
  label { color: var(--nd-tx60) !important; font-size: 12px !important; font-weight: 600 !important; }
  .form-select option { background: var(--nd-surface-3) !important; color: var(--nd-text) !important; }
  .modal-content { background: var(--nd-surface) !important; border: 1px solid var(--nd-w08) !important; border-radius: 20px !important; color: var(--nd-text) !important; }
  .modal-header { border-bottom: 1px solid var(--nd-w06) !important; background: transparent !important; }
  .modal-footer { border-top: 1px solid var(--nd-w06) !important; background: transparent !important; }
  .modal-title { color: var(--nd-text) !important; font-weight: 800 !important; }
  .btn-close { filter: var(--nd-close-filter) !important; }
  .dropdown-menu { background: var(--nd-surface-2) !important; border: 1px solid var(--nd-w08) !important; border-radius: 14px !important; box-shadow: var(--nd-shadow-strong) !important; }
  .dropdown-item { color: var(--nd-tx60) !important; font-family: 'DM Sans', sans-serif !important; }
  .dropdown-item:hover { background: var(--nd-w06) !important; color: var(--nd-text) !important; }
  .dropdown-divider { border-color: var(--nd-w06) !important; }
  .progress { background: var(--nd-w08) !important; border-radius: 8px !important; }
  .progress-bar { border-radius: 8px !important; }
  .spinner-border { color: #3B82F6 !important; }
  .bg-light { background: var(--nd-w05) !important; }
  .bg-white { background: var(--nd-w06) !important; }
  .text-dark { color: var(--nd-text) !important; }
  .text-muted, .text-secondary { color: var(--nd-tx35) !important; }
  .border { border-color: var(--nd-w06) !important; }
  .shadow-sm { box-shadow: 0 4px 16px rgba(0,0,0,0.25) !important; }
  .rounded-3 { border-radius: 10px !important; }
  .nav-outside.mob-backdrop::before { background: rgba(0,0,0,0.7) !important; backdrop-filter: blur(4px) !important; }
  /* Kill any lingering page-header / breadcrumb / bottom gaps */
  .page-header { display: none !important; }
  /* Datta Able adds padding-bottom to these — nuke them */
  .pcoded-main-container,
  .pcoded-wrapper,
  .pcoded-content,
  .pcoded-inner-content,
  .content-card-wrapper,
  .main-content-card { padding-bottom: 0 !important; margin-bottom: 0 !important; }
  /* Some builds add a bottom toolbar */
  .footer, .pcoded-footer, footer { display: none !important; }
`;

const AdminLayout = ({ children }) => {
  const windowSize = useWindowSize();
  const ref = useRef();
  const configContext = useContext(ConfigContext);
  const { collapseMenu, headerFixedLayout } = configContext.state;
  const { dispatch } = configContext;

  useEffect(() => {
    if (windowSize.width > 992 && windowSize.width <= 1024) dispatch({ type: actionType.COLLAPSE_MENU });
    if (windowSize.width < 992) dispatch({ type: actionType.CHANGE_LAYOUT, layout: 'vertical' });
  }, [dispatch, windowSize]);

  useOutsideClick(ref, () => { if (collapseMenu) dispatch({ type: actionType.COLLAPSE_MENU }); });

  const mobileOutClickHandler = () => {
    if (windowSize.width < 992 && collapseMenu) dispatch({ type: actionType.COLLAPSE_MENU });
  };

  let sidebarEl = <Navigation />;

  let mainContainer = (
    <div className="pcoded-main-container">
      <div className="pcoded-wrapper">
        <div className="pcoded-content">
          <div className="pcoded-inner-content">
            <div className="content-card-wrapper">
              {children}
                <Outlet />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (windowSize.width < 992) {
    let outSideClass = ['nav-outside'];
    if (collapseMenu)      outSideClass = [...outSideClass, 'mob-backdrop'];
    if (headerFixedLayout) outSideClass = [...outSideClass, 'mob-fixed'];

    sidebarEl = (
      <div className={outSideClass.join(' ')} ref={ref}>
        <Navigation />
      </div>
    );

    mainContainer = (
      <div
        role="button"
        tabIndex="0"
        className="pcoded-outside"
        onClick={mobileOutClickHandler}
        onKeyDown={mobileOutClickHandler}
      >
        {mainContainer}
      </div>
    );
  }

  return (
    <>
      <style>{NOVAA_SHELL_CSS}</style>
      {sidebarEl}
      {mainContainer}
    </>
  );
};

AdminLayout.propTypes = { children: PropTypes.node };
export default AdminLayout;
